import { ModifyActionItems, ModifyDecisions } from "@/interfaces/IRecap";
import i18n from "@/shared/i18n";
import { useAppActions } from "@/store/selectors/AppSelectors";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import QueryKeys from "../queryKeys";
import {
  deleteActionItem,
  deleteDecision,
  fetchActionItemsBySessionId,
  fetchDecisionsBySessionId,
  fetchKeypointsBySessionId,
  fetchMeetDataByKey,
  fetchMeetRecap,
  fetchOverviewBySessionId,
  fetchParticipantsBySessionId,
  fetchRecordingsBySessionId,
  fetchSessionDataBySessionId,
  fetchSessionsByMeetKey,
  getActionItemById,
  getDecisionById,
  modifyActionItem,
  modifyDecision,
  undoDeleteActionItem,
  undoDeleteDecision,
} from "../services/RecapServices";

export const useMeetDataByKey = (meetingId: string) =>
  useQuery({
    queryKey: [QueryKeys.recapMeet, meetingId],
    queryFn: () => fetchMeetDataByKey({ meetingId }),
    enabled: !!meetingId,
  });

export const useMeetRecap = (meetingId: string) =>
  useQuery({
    queryKey: [QueryKeys.recapMeet, meetingId],
    queryFn: () => fetchMeetRecap({ meetingId }),
    enabled: !!meetingId,
  });

export const useRecapSessionsByMeetKey = (meetingKey: string) =>
  useQuery({
    queryKey: [QueryKeys.recapSessionByMeetKey, meetingKey],
    queryFn: () => fetchSessionsByMeetKey({ meetingKey }),
    enabled: !!meetingKey,
  });

export const useSessionDataBySessionId = (sessionId: string) =>
  useQuery({
    queryKey: [QueryKeys.recapSessionDataBySessionId, sessionId],
    queryFn: () => fetchSessionDataBySessionId({ sessionId }),
    enabled: !!sessionId,
  });

export const useRecapRecordingsBySessionId = (sessionId: string) =>
  useQuery({
    queryKey: [QueryKeys.recapRecordingsBySessionId, sessionId],
    queryFn: () => fetchRecordingsBySessionId({ sessionId }),
    enabled: !!sessionId,
  });

export const useRecapParticipantsBySessionId = (sessionId: string) =>
  useQuery({
    queryKey: [QueryKeys.recapParticipantsBySessionId, sessionId],
    queryFn: () => fetchParticipantsBySessionId({ sessionId }),
    enabled: !!sessionId,
  });

export const useRecapKeypointsBySessionId = (sessionId: string) =>
  useQuery({
    queryKey: [QueryKeys.recapKeypointsBySessionId, sessionId],
    queryFn: () => fetchKeypointsBySessionId({ sessionId }),
    enabled: !!sessionId,
  });

export const useRecapActionItemsBySessionId = (
  sessionId: string,
  options?: { includeDeleted?: boolean }
) =>
  useQuery({
    queryKey: [
      QueryKeys.recapActionItemsBySessionId,
      sessionId,
      { includeDeleted: options?.includeDeleted ?? true },
    ],
    queryFn: () =>
      fetchActionItemsBySessionId({
        sessionId,
        includeDeleted: options?.includeDeleted ?? true,
      }),
    enabled: !!sessionId,
  });

export const useRecapActionItemById = (id: string) => {
  return useQuery({
    queryKey: [QueryKeys.recapActionItemById, id],
    queryFn: () => getActionItemById(id),
    enabled: !!id,
  });
};
export const useRecapModifyActionItem = () => {
  const queryClient = useQueryClient();
  const { setMessage } = useAppActions();

  return useMutation({
    mutationFn: (params: ModifyActionItems) => modifyActionItem(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.recapActionItemsBySessionId,
          variables.data.sessionId,
        ],
      });

      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.recapActionItemById, variables.id],
        });
      }

      setMessage({
        type: "success",
        text: variables.id
          ? i18n.t("recap.successfulEdit")
          : i18n.t("recap.successfulAdd"),
      });
    },

    onError: (error: any) => {
      setMessage({
        type: "error",
        text: error?.message || i18n.t("recap.failedSave"),
      });
    },
  });
};

export const useRecapDeleteActionItem = () => {
  const queryClient = useQueryClient();
  const { setMessage } = useAppActions();

  return useMutation({
    mutationFn: (params: { sessionId: string; id: string | number }) =>
      deleteActionItem(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.recapActionItemsBySessionId,
          variables.sessionId,
        ],
      });

      setMessage({
        type: "success",
        text: i18n.t("recap.successfulDelete"),
      });
    },

    onError: (error: any) => {
      setMessage({
        type: "error",
        text: error?.message || i18n.t("recap.failedDelete"),
      });
    },
  });
};

export const useRecapUndoDeleteActionItem = () => {
  const queryClient = useQueryClient();
  const { setMessage } = useAppActions();

  return useMutation({
    mutationFn: (params: { sessionId: string; id: string | number }) =>
      undoDeleteActionItem({ id: params.id }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.recapActionItemsBySessionId,
          variables.sessionId,
        ],
      });

      setMessage({
        type: "success",
        text: i18n.t("recap.successfulRestore"),
      });
    },

    onError: (error: any) => {
      setMessage({
        type: "error",
        text: error?.message || i18n.t("recap.failedRestore"),
      });
    },
  });
};

export const useRecapOverviewBySessionId = (sessionId: string) =>
  useQuery({
    queryKey: [QueryKeys.recapOverviewBySessionId, sessionId],
    queryFn: () => fetchOverviewBySessionId({ sessionId }),
    enabled: !!sessionId,
  });

export const useRecapDecisionsBySessionId = (
  sessionId: string,
  options?: { includeDeleted?: boolean }
) =>
  useQuery({
    queryKey: [
      QueryKeys.recapDecisionsBySessionId,
      sessionId,
      { includeDeleted: options?.includeDeleted ?? true },
    ],
    queryFn: () =>
      fetchDecisionsBySessionId({
        sessionId,
        includeDeleted: options?.includeDeleted ?? true,
      }),
    enabled: !!sessionId,
  });

export const useRecapDecisionById = (id: string) =>
  useQuery({
    queryKey: [QueryKeys.recapDecisionById, id],
    queryFn: () => getDecisionById(id),
    enabled: !!id,
  });

export const useRecapModifyDecision = () => {
  const queryClient = useQueryClient();
  const { setMessage } = useAppActions();

  return useMutation({
    mutationFn: (params: ModifyDecisions) => modifyDecision(params),

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.recapDecisionsBySessionId, variables?.sessionId],
      });

      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.recapDecisionById, variables?.id],
        });
      }

      setMessage({
        type: "success",
        text: variables.id
          ? i18n.t("recap.successfulEdit")
          : i18n.t("recap.successfulAdd"),
      });
    },

    onError: (error: any) => {
      setMessage({
        type: "error",
        text: error?.message || i18n.t("recap.failedSave"),
      });
    },
  });
};

export const useRecapDeleteDecision = () => {
  const queryClient = useQueryClient();
  const { setMessage } = useAppActions();

  return useMutation({
    mutationFn: (params: { sessionId: string; id: string | number }) =>
      deleteDecision({ id: params.id }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.recapDecisionsBySessionId,
          variables?.sessionId,
        ],
      });
      setMessage({
        type: "success",
        text: i18n.t("recap.successfulDelete"),
      });
    },

    onError: (error: any) => {
      setMessage({
        type: "error",
        text: error?.message || i18n.t("recap.failedDelete"),
      });
    },
  });
};

export const useRecapUndoDeleteDecision = () => {
  const queryClient = useQueryClient();
  const { setMessage } = useAppActions();

  return useMutation({
    mutationFn: (params: { sessionId: string; id: string | number }) =>
      undoDeleteDecision({ id: params.id }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          QueryKeys.recapDecisionsBySessionId,
          variables?.sessionId,
        ],
      });
      setMessage({
        type: "success",
        text: i18n.t("recap.successfulRestore"),
      });
    },

    onError: (error: any) => {
      setMessage({
        type: "error",
        text: error?.message || i18n.t("recap.failedRestore"),
      });
    },
  });
};
